/**
 * The feedback popup — one small panel on any public page (PROJECT-289 T0289.9).
 *
 * WHAT THE VISITOR DOES, IN ORDER: pick which part of the page they mean, say how it felt, and if
 * they want, add a line. That is the whole instrument.
 *
 * ⚠️ **THE SHAPE IS PROGRESSIVE, AND THE DATABASE IS WHY.** The first view is deliberately as small
 * as the reference the PO gave (one question and a box). Choosing a face then reveals the 1–10
 * number, because `feedback` carries `chk_feedback_signals_together`: a face and a number are stored
 * **together or not at all**, since the promotion trigger reads them as the visitor's own answer and
 * half of one cannot be read (`D-289-009`). So the panel cannot offer a face alone — and rather than
 * showing both controls to everyone, the number appears only for a visitor who already engaged.
 *
 * ⚠️ **THE SECTIONS COME FROM THE SERVER AND ARE FETCHED WHEN THE PANEL OPENS.** The ids are the
 * contract (they become the subject a promoted request is keyed to) and the server also validates
 * against them, so a list repeated here would disagree with the server the first time a section is
 * added. The fetch is lazy because a visitor may never open the panel, and a marketing page should
 * not spend a request on a panel nobody asked for.
 *
 * ⚠️ **THE LIST IS PER-PAGE, AND THIS PANEL IS NOT.** It is mounted once in `PublicLayout`, so it is
 * on every public route — but the landing page's blocks ("The free taste", "The Raise") are not on a
 * blog post. The server decides which list a path gets (`sections_for_path`); this component sends
 * the pathname and re-fetches when it changes, because a client-side navigation does not remount it.
 *
 * ⚠️ **NOTHING HERE IS PROMISED THAT WE CANNOT KEEP.** The panel collects no email address, so the
 * reply never offers one — the server's own sentence is shown instead of a client-side paraphrase,
 * because the server distinguishes "half a signal" from "rate limited" from "unknown section" in
 * wording a visitor can act on.
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { MessageSquare, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { posthogDistinctId } from "@/config/posthog";
import {
  ApiClientError,
  fetchFeedbackSections,
  submitFeedback,
  type FeedbackEmotion,
  type FeedbackSection,
} from "@/services/api/feedbackApi";

/** The three faces, in the order the visitor reads them: bad → fine → good. */
const FACES: { value: FeedbackEmotion; glyph: string; label: string }[] = [
  { value: -1, glyph: "😞", label: "Not good" },
  { value: 0, glyph: "😐", label: "Fine" },
  { value: 1, glyph: "🙂", label: "Good" },
];

const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export function FeedbackPopup() {
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState<FeedbackSection[]>([]);
  const [section, setSection] = useState("");
  const [emotion, setEmotion] = useState<FeedbackEmotion | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState<{ ok: boolean; text: string } | null>(null);

  const { pathname } = useLocation();
  // ⚠️ **WHICH PATH THE CURRENT `sections` WERE FETCHED FOR — THIS IS THE CACHE KEY, NOT
  // `sections.length`.** This panel is mounted ONCE in `PublicLayout`, so a client-side navigation
  // does not remount it; the effect below used to skip re-fetching as soon as any list had arrived
  // (`if (!open || sections.length) return`), which meant a visitor who opened the panel on the
  // landing page and then walked to a blog post kept the LANDING PAGE'S blocks. Measured in a
  // browser 2026-10-11: `/blog/multi-agent-supervisor-architecture` offered "The free taste".
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  // ⚠️ THE OPTIONS CAN FAIL, AND THAT IS A STATE THE PANEL HAS TO SHOW. Without it a failed fetch
  // was indistinguishable from a slow one, forever — see the select's own comment below.
  const [sectionsFailed, setSectionsFailed] = useState(false);
  // Incremented only by "Try again". It is a DEPENDENCY of the fetch effect rather than a cache key
  // because the key already holds the value the retry would set (see `retrySections`).
  const [reloadToken, setReloadToken] = useState(0);

  // The options arrive when the panel opens, once per page. A failure is not thrown at the visitor
  // as a stack trace — but it MUST be visible in the panel, because a dropdown stuck on "Loading…"
  // is a dead end with no explanation (loop step 16).
  useEffect(() => {
    if (!open || loadedFor === pathname) return;
    let alive = true;
    setSectionsFailed(false);
    fetchFeedbackSections(pathname)
      .then((list) => {
        if (!alive) return;
        setSections(list);
        setLoadedFor(pathname);
        // A choice made on the previous page can be absent from this page's list ("The free taste"
        // is not on a blog post). Keeping it would post a section this page never offered, and
        // `other` is the honest landing place rather than a silent mis-tag.
        setSection((cur) => (cur && !list.some((s) => s.id === cur) ? "" : cur));
      })
      .catch((err) => {
        if (!alive) return;
        setSectionsFailed(true);
        console.warn("[FeedbackPopup] could not load sections", err);
      });
    return () => {
      alive = false;
    };
  }, [open, pathname, loadedFor, reloadToken]);

  /** Re-run the options fetch after a failure.
   *
   *  ⚠️ **A NO-OP WAS SHIPPED HERE FIRST, AND THE BROWSER CHECK IS WHAT CAUGHT IT.** The original
   *  version cleared the cache key — `setLoadedFor(null)` — which is the right idea and does nothing
   *  at all: the key is set only on SUCCESS, so after a FAILURE it is ALREADY `null`, React bails out
   *  of the identical state update, no re-render happens, and the effect never runs again. Measured
   *  2026-10-12: the button was present, said "Try again", and left the list on "Could not load the
   *  list" forever — a control that promises without performing, which is worse than no button.
   *
   *  A token that always CHANGES is the fix: an increment cannot be a no-op, so the effect is
   *  guaranteed to re-run. The guard above still decides whether it should, so this stays one code
   *  path rather than a second fetch that could drift from the first.
   */
  const retrySections = useCallback(() => {
    setSectionsFailed(false);
    setReloadToken((n) => n + 1);
  }, []);

  const reset = useCallback(() => {
    setSection("");
    setEmotion(null);
    setRating(null);
    setMessage("");
    setReply(null);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  // A face AND a number, or neither — the server refuses half a pair. So the button is enabled only
  // when the pair is complete, or when the visitor wrote something instead.
  const pairComplete = emotion !== null && rating !== null;
  const canSend = !!section && (pairComplete || message.trim().length > 0);

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setReply(null);
    try {
      const res = await submitFeedback({
        section,
        // Omit both rather than sending a half pair: a bare comment is a legal shape, half a signal
        // is not.
        ...(pairComplete ? { emotion: emotion as FeedbackEmotion, rating: rating as number } : {}),
        message: message.trim() || undefined,
        page_url: window.location.href,
        // The consented lane only — `posthogDistinctId` returns undefined on the cookieless base
        // lane, and that is the guard doing its job rather than a missing value.
        posthog_distinct_id: posthogDistinctId(),
      });
      // ⚠️ BOTH HALVES OF THE ANSWER ARE CHECKED, AND THE SECOND ONE IS NOT REDUNDANT.
      //
      // This estate's `ApiResponse` envelope puts the outcome in the BODY (`status`/`statusCode`)
      // and other routes return it as FastAPI's default **HTTP 200** — so a client that trusts the
      // HTTP status alone is told a refusal succeeded. The route for this panel now sets a real
      // status too, which is why `apiClient`'s throw lands in the catch below; but a public surface
      // is the worst place to depend on ONE of two conventions holding, and the failure mode is the
      // worst one available: a visitor shown *"thank you"* for a message the server refused.
      //
      // So a body that says `status: false` is treated as a refusal HERE, whatever the HTTP status
      // said. `res.message` is the server's own sentence in both branches, which is the whole point
      // of sending it.
      if (!res?.status) {
        setReply({ ok: false, text: res?.message || "That did not go through — please try again." });
        return;
      }
      setReply({ ok: true, text: res.message });
      // The form is cleared but the panel stays open, so the visitor can see the confirmation
      // rather than having the window vanish under their cursor.
      setEmotion(null);
      setRating(null);
      setMessage("");
    } catch (err) {
      // ⚠️ **A NETWORK FAILURE MUST NOT BE SHOWN AS THE BROWSER'S OWN STRING.**
      //
      // `apiClient` wraps a failed `fetch` as `` `Request failed: ${err.message}` ``, and on every
      // browser that message is the literal *"Failed to fetch"*. Measured 2026-10-11 on the DEV
      // build: a visitor saw exactly `Request failed: Failed to fetch` while the backend was being
      // redeployed, and it tells them nothing they can act on — not what happened, not whether their
      // words survived, not whether to try again.
      //
      // `RULES_FE_QA` loop step 16 makes this a REQUIRED run — *"an unavailable camera/microphone/
      // fetch … asserting an honest sentence + usable controls + zero console noise"* — and this
      // panel had never been through it. The step is the reason this branch exists.
      //
      // `status === 0` is `ApiClientError`'s own way of saying "there was no HTTP response at all",
      // and the distinction decides the words: a REFUSAL carries the server's sentence and repeating
      // it is right, while a failure that never reached a server has none, and the platform's is not
      // ours to show. The visitor's words are NOT cleared on this path (they are cleared only on
      // success), so the sentence may promise that honestly.
      const offline = err instanceof ApiClientError && err.status === 0;
      const text = offline
        ? "We could not reach the server — what you wrote is still here. Please try again in a moment."
        : err instanceof ApiClientError
          ? err.message
          : "Could not send that — please try again in a moment.";
      setReply({ ok: false, text });
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 left-4 z-40 flex items-center gap-2 rounded-full border border-gray-300 bg-white/95 px-3 py-2 text-sm text-gray-700 shadow-lg backdrop-blur transition hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"
        aria-label="Tell us what you think of this page"
      >
        <MessageSquare className="h-4 w-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-4 left-4 z-40 w-[min(92vw,22rem)] rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
      role="dialog"
      aria-label="Tell us what you think of this page"
    >
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {section
              ? "What can we do to improve this?"
              : "Which part of the page is this about?"}
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {/* ⚠️ WHAT THIS SENTENCE MAY AND MAY NOT CLAIM, and it used to claim too much.
                It said *"kept and read"* — and **the READ half is not true for every message yet**:
                a NEGATIVE one is genuinely read (the promotion bridge files a request a seat acts
                on), while a positive or neutral one is STORED and its reader is the classification
                step, which is `@P289` `T0289.29` — a row the PO PARKED when he cut this project back
                to the popup. So *"read"* described the design, not the running system.
                **The estate's own rule for this panel is that it promises only what it can keep**
                (`T0289.24`, and `FTUE`'s positivity law behind it), so the sentence now states what
                is true today and says the true half of the intent beside it: a complaint becomes
                work, and what is written reaches the people building this. When `T0289.29` lands,
                the stronger word earns its place back — and this comment is how it gets noticed. */}
            What you send is kept — not counted and discarded. A complaint becomes
            work; everything you write reaches the people building this.
          </p>
        </div>
        <button
          type="button"
          onClick={close}
          className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3 px-4 py-3">
        {/* 1 — WHICH PART. The PO's own requirement: the visitor names the item, so the feedback
            says what it is about instead of arriving with only a URL. */}
        <div>
          <label htmlFor="fb-section" className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
            Part of the page
          </label>
          <select
            id="fb-section"
            value={section}
            onChange={(e) => setSection(e.target.value)}
            disabled={!sections.length}
            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
          >
            <option value="">
              {/* ⚠️ THREE STATES, NOT TWO, AND THE THIRD IS THE ONE THAT WAS MISSING.
                  This read `sections.length ? "Choose one…" : "Loading…"`, so when the fetch FAILED
                  the visitor was left on "Loading…" for good, with `Send` permanently disabled and
                  nothing saying why — measured 2026-10-11 with `/sections` aborted. `RULES_FE_QA`
                  loop step 16 forbids exactly that ("never a blank page or an endless splash": an
                  unavailable fetch must still yield an honest sentence and usable controls). The
                  wording is the visitor's, not ours: it says what happened and what to do. */}
              {sections.length
                ? "Choose one…"
                : sectionsFailed
                  ? "Could not load the list"
                  : "Loading…"}
            </option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          {/* The honest sentence AND the way out. A disabled control with no explanation is the
              dead end loop step 16 forbids; a sentence plus a retry is the same failure handled. */}
          {sectionsFailed && (
            <p className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              We could not load the list of page parts.
              <button
                type="button"
                onClick={retrySections}
                className="underline underline-offset-2 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Try again
              </button>
            </p>
          )}
        </div>

        {/* 2 — HOW IT FELT. Choosing a face reveals the number, because the pair is stored
            together or not at all. */}
        {section && (
          <div>
            <span className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
              How does it feel? <span className="text-gray-400">(optional)</span>
            </span>
            <div className="flex gap-2">
              {FACES.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => {
                    // Tapping the same face again clears it, so a mis-tap costs one tap and the
                    // visitor can get back to a plain comment.
                    const next = emotion === f.value ? null : f.value;
                    setEmotion(next);
                    if (next === null) setRating(null);
                  }}
                  aria-pressed={emotion === f.value}
                  title={f.label}
                  className={
                    "flex-1 rounded-lg border py-2 text-xl transition " +
                    (emotion === f.value
                      ? "border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800"
                      : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800")
                  }
                >
                  {f.glyph}
                  <span className="mt-0.5 block text-[10px] text-gray-500 dark:text-gray-400">
                    {f.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {emotion !== null && section && (
          <div>
            <span className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
              1 is poor, 10 is excellent
            </span>
            <div className="flex flex-wrap gap-1">
              {RATINGS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(rating === n ? null : n)}
                  aria-pressed={rating === n}
                  className={
                    "h-7 w-7 rounded border text-xs transition " +
                    (rating === n
                      ? "border-gray-900 bg-gray-900 text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800")
                  }
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 3 — WHAT THEY WANT TO SAY. Optional when a face and number were given, required
            otherwise: a row with nothing in it is not feedback. */}
        {section && (
          <div>
            <label htmlFor="fb-message" className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
              Anything you want to add?{" "}
              {pairComplete ? (
                <span className="text-gray-400">(optional)</span>
              ) : (
                <span className="text-gray-400">(needed if you skip the faces)</span>
              )}
            </label>
            <Textarea
              id="fb-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What would make this better?"
            />
          </div>
        )}

        {reply && (
          <p
            className={
              "text-xs " +
              (reply.ok ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400")
            }
            role="status"
          >
            {reply.text}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 pt-1">
          {/* Says what is still missing rather than leaving a dead button, so the visitor is never
              left guessing why Send does nothing. */}
          <span className="text-[11px] text-gray-400">
            {!section
              ? "Pick a part of the page first"
              : canSend
                ? ""
                : "Add a face and a number, or write something"}
          </span>
          <Button size="sm" onClick={send} disabled={!canSend || sending}>
            {sending ? "Sending…" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default FeedbackPopup;
